import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Check, Languages, Sparkles } from 'lucide-react';
import { useVoice, SUPPORTED_LANGUAGES } from '../context/VoiceContext';
import { GlassCard } from './layout/GlassCard';

export default function LanguageSelector() {
  const { 
    language, 
    setLanguage, 
    autoDetectLanguage, 
    setAutoDetectLanguage,
    detectedLanguage,
    testVoice
  } = useVoice();

  const languages = Object.values(SUPPORTED_LANGUAGES);

  // Group languages by region
  const languageGroups = {
    'Indian Languages': languages.filter(l => ['hi', 'bn', 'mr', 'pa', 'ta', 'te', 'ml'].includes(l.id)),
    'European Languages': languages.filter(l => ['en', 'es', 'de', 'fr', 'pt', 'ru'].includes(l.id)),
    'Asian Languages': languages.filter(l => ['ja', 'zh', 'ko'].includes(l.id)),
    'Middle Eastern': languages.filter(l => ['ar', 'ur'].includes(l.id)),
  };

  return (
    <GlassCard className="p-6" data-testid="language-selector">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
          <Globe className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-[var(--text-primary)]">Language Settings</h3>
          <p className="text-sm text-[var(--text-tertiary)]">Choose your preferred language for voice and text</p>
        </div>
      </div>

      {/* Auto-detect Toggle */}
      <div className="mb-6 p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Languages className="w-5 h-5 text-accent" />
            <div>
              <p className="font-medium text-[var(--text-primary)]">Auto-detect Language</p>
              <p className="text-xs text-[var(--text-tertiary)]">
                AIRA will automatically detect and respond in your language
              </p>
            </div>
          </div>
          <button
            onClick={() => setAutoDetectLanguage(!autoDetectLanguage)}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              autoDetectLanguage ? 'bg-gradient-to-r from-primary to-secondary' : 'bg-gray-600'
            }`}
            data-testid="auto-detect-toggle"
          >
            <motion.div
              className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
              animate={{ left: autoDetectLanguage ? '32px' : '4px' }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </button>
        </div>
        
        {autoDetectLanguage && detectedLanguage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex items-center gap-2 text-sm text-accent"
          >
            <Sparkles className="w-4 h-4" />
            <span>Detected: {SUPPORTED_LANGUAGES[detectedLanguage]?.nativeName || detectedLanguage}</span>
          </motion.div>
        )}
      </div>

      {/* Manual Language Selection */}
      <div className={`transition-opacity ${autoDetectLanguage ? 'opacity-50' : 'opacity-100'}`}>
        <p className="text-sm text-[var(--text-secondary)] mb-4">
          {autoDetectLanguage ? 'Or manually select a language:' : 'Select your preferred language:'}
        </p>

        {Object.entries(languageGroups).map(([groupName, groupLanguages]) => (
          <div key={groupName} className="mb-4">
            <p className="text-xs text-[var(--text-tertiary)] uppercase tracking-wider mb-2">{groupName}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {groupLanguages.map((lang) => (
                <motion.button
                  key={lang.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setLanguage(lang.id)}
                  className={`relative flex items-center gap-2 p-3 rounded-xl border transition-all ${
                    language === lang.id && !autoDetectLanguage
                      ? 'bg-gradient-to-r from-primary/20 to-secondary/20 border-primary/50'
                      : 'bg-[var(--bg-card)] border-[var(--border-color)] hover:border-primary/30'
                  }`}
                  data-testid={`language-${lang.id}`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-primary)] truncate">{lang.nativeName}</p>
                    <p className="text-xs text-[var(--text-tertiary)] truncate">{lang.name}</p>
                  </div>
                  {language === lang.id && !autoDetectLanguage && (
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Test Button */}
      <div className="mt-6 pt-4 border-t border-[var(--border-color)]">
        <button
          onClick={testVoice}
          className="btn-secondary w-full flex items-center justify-center gap-2"
          data-testid="test-language-btn"
        >
          <Languages className="w-4 h-4" />
          Test Voice in {SUPPORTED_LANGUAGES[language]?.nativeName || 'English'}
        </button>
      </div>
    </GlassCard>
  );
}
